import { Component, OnInit } from "@angular/core";
import { BroadcastService, ObjectUtils, ToasterType } from "@cv-invision/core";
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ActivatedRoute } from "@angular/router";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IntegrationsUrlParamsModel } from "../../models/integrations-url-params.model";
import { OutgoingEventsHybridService } from "../../../outgoing-events-hybrid/service/outgoing-events-hybrid.service";
import { buildExtraData, buildModel, ExtraDataConfig, HYBRID_TAB_DISPLAY_NAMES, HYBRID_TAB_NAMES, HybridDataModel, HybridTabName, OutgoingEventHybridModel } from "../../models/outgoing-events.model";

@Component({
	selector: 'cv-invision-generic-outgoing-events-hybrid-tabs',
	templateUrl: './outgoing-events-hybrid-tabs.component.html',
	styleUrls: ['./outgoing-events-hybrid-tabs.component.scss']
})
export class OutgoingEventsHybridTabsComponent implements OnInit {
	public form: FormGroup;
	public params = new IntegrationsUrlParamsModel();
	public integrationId: string;
	public model: OutgoingEventHybridModel = null;
	public extraData: ExtraDataConfig = null;
	public formBuilderConfig: any = null;
	public saving: boolean = false;
	public isEditing: boolean = false;
	public eventNameOptions: { value: any; label: any; }[] = [];
	public formArrayNames = HYBRID_TAB_NAMES;
  	public tabDisplayNames = HYBRID_TAB_DISPLAY_NAMES;
	public selectedTabIndex = 0;
	public hybridData: HybridDataModel;
	public config: any = { optionsPostIf: [] };
	public originalFormValue: any;
	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		private route: ActivatedRoute,
		private outgoingEventHybridService: OutgoingEventsHybridService,
		private broadcastService: BroadcastService,
		private fb: FormBuilder
	) { }

	ngOnInit(): void {
		this.form = this.fb.group({
			product: this.fb.array([]),
			productHybrid: this.fb.array([]),
			productNonHybrid: this.fb.array([]),
		});

		this.hybridData = new HybridDataModel();

		this.route.params
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(params => {
				this.params = new IntegrationsUrlParamsModel();
				this.integrationId = this.route.snapshot.paramMap.get('organizationIntegrationId')? this.route.snapshot.paramMap.get('organizationIntegrationId') : params.integrationId;
				this.loadDataOutgoingEventHybrid();
			});
	}

	/**
	 * Loads outgoing event data for the current integration and populates the form arrays.
	 */
	private loadDataOutgoingEventHybrid() {
	this.outgoingEventHybridService
		.getIntegrationOutgoingEventsbyOrganizationId(this.integrationId)
		.pipe(takeUntil(this.atComponentDestroy))
		.subscribe(result => {
			this.hybridData = new HybridDataModel(result.data);

			// Cast cmpRelation as Record<string, string>
			const cmpRelationMap: Record<string, string> = result.cmpRelation;

			// Build eventNameOptions directly from cmpRelation entries
			this.eventNameOptions = Object.entries(cmpRelationMap).map(([key, label]) => ({
				value: key,
				label: label
			}));

			// Create cmpRelation options for dropdown config
			const cmpRelation = this.eventNameOptions;

			this.config = {
				optionsPostIf: result?.postValues || []
			};

			this.model = buildModel(this.hybridData.productHybrid, this.config.optionsPostIf);
			this.formBuilderConfig = this.model.formBuilderConfig;
			this.extraData = buildExtraData(cmpRelation);

			this.formArrayNames.forEach(name => {
				this.setFormArray(name, this.hybridData[name]);
			});

			this.originalFormValue = ObjectUtils.recursiveDeepCopy(this.form.value);
			this.form.markAsPristine();
		});
	}
	

	/**
	 * Sets the FormArray for a given tab with provided data.
	 * @param key - The name of the form array ('product', 'productHybrid', or 'productNonHybrid').
	 * @param data - The array of event objects to populate the form array.
	 */
	private setFormArray(key: 'product' | 'productHybrid' | 'productNonHybrid', data: any[]) {
		const control = this.form.get(key) as FormArray;
		control.clear();
		data.forEach(item => {
			control.push(this.fb.group({
				eventName: [item.eventName, Validators.required],
				postIf: [item.postIf, Validators.required],
				enabled: [this.toBoolean(item.enabled) ?? false]
			}));
		});
	}

	/**
	 * Converts a value to boolean.
	 * @param value - The value to convert.
	 * @returns `true` if value is boolean true or string 'true'; otherwise, false.
	 */
	private toBoolean(value: any): boolean {
		return value === true || value === 'true';
	}

	/**
	 * Returns the current form value (payload).
	 * @returns The form's value object.
	 */
	getCurrentPayload() {
		return this.form.value;
	}

	/**
	 * Saves the form data using the service, shows success/error toasts, and reloads data.
	 * @param formData - The data to save.
	 * @param isEditMode - Whether the save is in edit mode.
	 * @param integrationId - The ID of the current integration.
	 */
	onSave(formData: any, isEditMode: boolean, integrationId: string): void {
		this.saving = true;
		this.isEditing = isEditMode;

		this.outgoingEventHybridService
			.saveOutgoingEvents(isEditMode, integrationId, formData)
			.subscribe({
				next: (response) => {
					this.saving = false;
					if (response) {
						this.showToast(`The Outgoing Event Was ${this.isEditing ? 'Updated' : 'Created'} Successfully`, ToasterType.SUCCESS);
						this.loadDataOutgoingEventHybrid();
						this.form.markAsPristine();
					} else {
						this.showToast('Failed to save outgoing events.', ToasterType.ERROR);
					}
				},
				error: (err) => {
					this.saving = false;
					this.showToast('An unexpected error occurred while saving.', ToasterType.ERROR);
					console.error(err);
				}
			});
	}

	/**
	 * Displays a toast notification.
	 * @param message - The message to show.
	 * @param type - The type of the toast (e.g. success or error).
	 */
	private showToast(message: string, type: ToasterType): void {
		this.broadcastService.broadcast({
			name: type,
			event: { message }
		});
	}

	/**
	 * Adds or removes a product form group from the specified tab's form array.
	 * @param type - The tab type.
	 * @param action - The action to perform: 'add' or 'delete'.
	 * @param index - Optional index used when deleting.
	 */
	onModifyProduct(type: HybridTabName, action: 'add' | 'delete', index?: number): void {
		const control = this.form.get(type) as FormArray;
		if (!control) return;

		if (action === 'add') {
			const defaultEventName = this.eventNameOptions?.[0]?.value ?? null;
    		const defaultPostIf = this.config?.optionsPostIf?.[0] ?? null;

			control.push(this.fb.group({
				eventName: [defaultEventName, Validators.required],
				postIf: [defaultPostIf, Validators.required],
				enabled: [true]
			  }));
		} else if (action === 'delete' && typeof index === 'number') {
			control.removeAt(index);
		}
	}

	/**
	 * Gets CMP relation options used in form fields.
	 */
	get cmpRelationOptions() {
		return this.extraData?.cmpRelation ?? [];
	}

	/**
	 * Returns the currently selected tab key.
	 */
	get selectedTab(): string {
		return this.formArrayNames[this.selectedTabIndex];
	}

	/**
	 * Retrieves the controls from a specified form array.
	 * @param key - The form array key.
	 * @returns An array of FormGroups.
	 */
	getFormArrayControls(key: HybridTabName): FormGroup[] {
		const control = this.form.get(key);
		return control instanceof FormArray ? (control.controls as FormGroup[]) : [];
	}

	/**
	 * Gets the enabled FormControl for a given tab and index.
	 * @param tab - The tab name.
	 * @param index - The index of the item.
	 * @returns The 'enabled' FormControl.
	 */
	getEnabledControl(tab: HybridTabName, index: number): FormControl {
		return this.form.get([tab, index, 'enabled']) as FormControl;
	}

	/**
	 * Determines if the form can be saved.
	 * @returns true if the form is valid and has unsaved changes (dirty).
	 */
	canSave(): boolean {
		return this.form.valid && ObjectUtils.recursiveDeepCopy(this.form.value) !== this.originalFormValue;
	}

	/**
	 * Submits the form after validation. Triggers save action.
	 */
	submitForm(): void {
		if (!this.canSave()) {
		  // Optionally mark all touched to show errors if invalid
		  this.form.markAllAsTouched();
		  return;
		}
	  
		const formData = this.getCurrentPayload();
		this.onSave(formData, this.isEditing, this.integrationId);
	}
}
